import { combineValidators } from '@spotify-internal/adstudio-web-utils/lib/utils/combineValidators';
import { required } from '@spotify-internal/adstudio-web-utils/lib/validators';
import { TFunction } from 'i18next';

const withCustomMessage = (
  // eslint-disable-next-line no-unused-vars
  validator: (value: string) => string | undefined,
  message: string,
) => {
  return (value: string) => validator(value) && message;
};

export const characterCountValidator = ({
  i18n,
  minCharacters,
  maxCharacters,
}: {
  i18n: string;
  minCharacters: number;
  maxCharacters: number;
}) => (val: string) => {
  const characterCount = val.length;

  if (characterCount < minCharacters || characterCount > maxCharacters) {
    return i18n;
  }

  return undefined;
};

export const wordCountValidator = ({
  i18n,
  minWords,
  maxWords,
}: {
  i18n: string;
  minWords: number;
  maxWords: number;
}) => (val: string) => {
  // Count words by splitting on whitespace and filtering out empty strings
  const words = val
    .trim()
    .split(/\s+/)
    .filter(word => word.length > 0);
  const wordCount = words.length;

  if (wordCount < minWords || wordCount > maxWords) {
    return i18n;
  }

  return undefined;
};

export const noSpecialCharactersValidator = ({ i18n }: { i18n: string }) => (
  val: string,
) => {
  const trimmedVal = val.trim();

  if (!trimmedVal) {
    return undefined;
  }

  const hasAlphanumeric = /[a-zA-Z0-9]/.test(trimmedVal);

  if (!hasAlphanumeric) {
    return i18n;
  }

  const hasDisallowedChars = /[^a-zA-Z0-9\s\-.'&,+]/.test(trimmedVal);

  if (hasDisallowedChars) {
    return i18n;
  }

  return undefined;
};

// required field validation
export const i18nRequired = (i18n: string) => withCustomMessage(required, i18n);

export const mainMessageValidator = (t: TFunction) => {
  return combineValidators([
    i18nRequired(t('Add a main message')),
    wordCountValidator({
      i18n: t('Main message must be between 2 and 65 words'),
      minWords: 2,
      maxWords: 65,
    }),
  ]);
};

export const businessNameValidator = (t: TFunction) => {
  return combineValidators([
    i18nRequired(t('Add a business name')),
    noSpecialCharactersValidator({
      i18n: t('Name your business without special characters or emojis.'),
    }),
    characterCountValidator({
      i18n: t('Business name must be less than 95 characters'),
      minCharacters: 1,
      maxCharacters: 95,
    }),
  ]);
};

export const createMainMessageValidatorWithBackendCheck = (
  generatedAdError: string | null,
  clearBackendError: () => void,
) => {
  return (t: TFunction) => (value: string) => {
    if (generatedAdError && value) {
      clearBackendError();
    }

    if (generatedAdError && value) return generatedAdError;

    const formValidationError = mainMessageValidator(t)(value);
    if (formValidationError) return formValidationError;

    return undefined;
  };
};
